import { ButtonSecondary, Colors, ConfirmDialog, Icon, type OptionProps } from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
    TemplateListModal,
    type TemplateListTile,
} from './components/AmelioActiveTemplates/TemplateListModal';
import { TemplateQuestionModal } from './components/AmelioActiveTemplates/TemplateQuestionModal';
import { ManageTemplates } from './components/ManageTemplates';
import { MeetingPlanCatalogModal } from './components/MeetingPlanTemplateSection/MeetingPlanCatalogModal';
import { NewMeetingPlanModal } from './components/MeetingPlanTemplateSection/NewMeetingPlanModal';

import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import {
    useAmelioReviewTemplates,
    useCreateReviewTemplate,
    useDeleteReviewTemplate,
    useGetReviewTemplate,
    useManageReviewTemplates,
    useUpdateReviewTemplate,
} from '@/api/review-services/review-template';
import { useImportAmelioTemplate } from '@/api/template-service';
import { useGetEditableMeetingTemplates } from '@/api/thematic-meetings';
import { queryClient } from '@/AppWithProviders';
import { CommonActions } from '@/constants/Actions';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { ReviewTemplateQueryKey } from '@/constants/QueryKeys';
import { ManagerAssessmentSkillSelectorOption } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName, type RouteNameType } from '@/navigation/types';
import { showSuccessToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';
import { flattenPaginatedData } from '@/utils/pagination';
import { ProGateOutcome } from '@/utils/performanceTier';

const INITIAL_TEMPLATES_PAGE_SIZE = 5;
const SUBSEQUENT_TEMPLATES_PAGE_SIZE = 6;

export const ManageTemplatesPage = () => {
    useEffect(() => {
        queryClient.resetQueries({
            queryKey: [ReviewTemplateQueryKey.TEMPLATE, EvaluationType.EVALUATION_360],
        });
        queryClient.resetQueries({
            queryKey: [ReviewTemplateQueryKey.TEMPLATE, EvaluationType.MANAGER_EVALUATION],
        });
        queryClient.resetQueries({
            queryKey: [ReviewTemplateQueryKey.TEMPLATE, EvaluationType.SELF_EVALUATION],
        });
    }, []);

    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const replaceDynamicTags = useReplaceDynamicTags();
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const cycleEvaluationId = searchParams.get('cycleEvaluationId');

    const { handleError } = useError();
    // Manager Conversation templates are Performance PRO (Meetings): a locked or hidden section reads
    // nothing and offers neither the catalogue nor a new plan.
    const canUseMeetingPlans = useProGate() === ProGateOutcome.Show;
    const [isTemplateListOpen, setIsTemplateListOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const [activeTemplateType, setActiveTemplateType] = useState<EvaluationTypeValue>(
        EvaluationType.EVALUATION_360,
    );

    // Kept after the preview closes so its content stays in place while the dialog fades out.
    const [previewedTemplate, setPreviewedTemplate] = useState<ReviewTemplate | undefined>(
        undefined,
    );
    const [templateToDelete, setTemplateToDelete] = useState<ReviewTemplate | undefined>(undefined);
    const [isNewMeetingPlanOpen, setIsNewMeetingPlanOpen] = useState(false);
    const [isMeetingPlanCatalogOpen, setIsMeetingPlanCatalogOpen] = useState(false);

    const { mutateAsync: importAmelioTemplate, isPending: isImportingAmelioTemplate } =
        useImportAmelioTemplate();

    const [creatingTemplateType, setCreatingTemplateType] = useState<EvaluationTypeValue | null>(
        null,
    );

    const { mutateAsync: createTemplate, isPending: isCreatingTemplate } =
        useCreateReviewTemplate();
    const { mutateAsync: updateTemplate } = useUpdateReviewTemplate();
    const { mutateAsync: deleteTemplate, isPending: isDeletingTemplate } =
        useDeleteReviewTemplate();

    const {
        data: templates360,
        isFetching: isFetchingTemplates360,
        isFetchNextPageError: isFetchingNextPageTemplates360Error,
        hasNextPage: hasMoreTemplates360,
        isFetchingNextPage: isFetchingNextPageTemplates360,
        isError: isFetchingTemplates360Error,
        fetchNextPage: fetchMore360Templates,
    } = useManageReviewTemplates({
        pageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        initialPageSize: INITIAL_TEMPLATES_PAGE_SIZE,
        subsequentPageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        meta: {
            reviewType: EvaluationType.EVALUATION_360,
        },
    });

    const {
        data: templatesSelfEvaluation,
        isFetching: isFetchingTemplatesSelfEvaluation,
        isFetchNextPageError: isFetchingNextPageTemplatesSelfEvaluationError,
        hasNextPage: hasMoreTemplatesSelfEvaluation,
        isFetchingNextPage: isFetchingNextPageTemplatesSelfEvaluation,
        isError: isFetchingTemplatesSelfEvaluationError,
        fetchNextPage: fetchMoreSelfReviewTemplates,
    } = useManageReviewTemplates({
        pageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        initialPageSize: INITIAL_TEMPLATES_PAGE_SIZE,
        subsequentPageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        meta: {
            reviewType: EvaluationType.SELF_EVALUATION,
        },
    });

    const {
        data: templatesManagerEvaluation,
        isFetching: isFetchingTemplatesManagerEvaluation,
        isFetchNextPageError: isFetchingNextPageTemplatesManagerEvaluationError,
        hasNextPage: hasMoreTemplatesManagerEvaluation,
        isFetchingNextPage: isFetchingNextPageTemplatesManagerEvaluation,
        isError: isFetchingTemplatesManagerEvaluationError,
        fetchNextPage: fetchMoreManagerReviewTemplates,
    } = useManageReviewTemplates({
        pageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        initialPageSize: INITIAL_TEMPLATES_PAGE_SIZE,
        subsequentPageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        meta: {
            reviewType: EvaluationType.MANAGER_EVALUATION,
        },
    });

    const {
        data: amelio360templates,
        isError: isFetchingAmelio360TemplatesError,
        isLoading: isLoadingAmelio360Templates,
    } = useAmelioReviewTemplates(EvaluationType.EVALUATION_360);

    const {
        data: amelioSelfReviewTemplates,
        isError: isFetchingAmelioSelfReviewTemplatesError,
        isLoading: isLoadingAmelioSelfReviewTemplates,
    } = useAmelioReviewTemplates(EvaluationType.SELF_EVALUATION);

    const {
        data: amelioManagerReviewTemplates,
        isError: isFetchingAmelioManagerReviewTemplatesError,
        isLoading: isLoadingAmelioManagerReviewTemplates,
    } = useAmelioReviewTemplates(EvaluationType.MANAGER_EVALUATION);

    const {
        data: meetingPlanTemplates,
        isLoading: isLoadingMeetingPlanTemplates,
        isError: isFetchingMeetingPlanTemplatesError,
    } = useGetEditableMeetingTemplates({ enabled: canUseMeetingPlans });

    const {
        data: selectedAmelioReviewTemplate,
        isFetching: isFetchingSelectedAmelioTemplateQuestions,
        isError: isFetchingSelectedAmelioTemplateQuestionsError,
    } = useGetReviewTemplate(previewedTemplate?.id);

    const company360Templates: ReviewTemplate[] = useMemo(
        () => flattenPaginatedData(templates360) || [],
        [templates360],
    );

    const companySelfTemplates: ReviewTemplate[] = useMemo(
        () => flattenPaginatedData(templatesSelfEvaluation) || [],
        [templatesSelfEvaluation],
    );

    const companyManagerTemplates: ReviewTemplate[] = useMemo(
        () => flattenPaginatedData(templatesManagerEvaluation) || [],
        [templatesManagerEvaluation],
    );

    const modalTemplates: ReviewTemplate[] = useMemo(() => {
        if (activeTemplateType === EvaluationType.EVALUATION_360) {
            return amelio360templates || [];
        }

        if (activeTemplateType === EvaluationType.MANAGER_EVALUATION) {
            return amelioManagerReviewTemplates || [];
        }

        return amelioSelfReviewTemplates || [];
    }, [
        activeTemplateType,
        amelio360templates,
        amelioManagerReviewTemplates,
        amelioSelfReviewTemplates,
    ]);

    const templateListTitle = () => {
        if (activeTemplateType === EvaluationType.EVALUATION_360) {
            return t('templateManagement.template.importTemplatemodal.import360ReviewTemplate');
        }

        if (activeTemplateType === EvaluationType.MANAGER_EVALUATION) {
            return t(
                'templateManagement.template.importTemplatemodal.importManagerReviewTemplate',
                { managerLabel: getManagerLabel() },
            );
        }

        return t('templateManagement.template.importTemplatemodal.importSelfReviewTemplate');
    };

    const toTemplateListTile = (template: ReviewTemplate): TemplateListTile => ({
        title: replaceDynamicTags({ text: getLocalizedText(template.title) }),
        description: template.description || '',
        questionCount: template.questions?.length ?? 0,
    });

    const onImport360Template = () => {
        setActiveTemplateType(EvaluationType.EVALUATION_360);
        setIsTemplateListOpen(true);
    };

    const onImportSelfReviewTemplate = () => {
        setActiveTemplateType(EvaluationType.SELF_EVALUATION);
        setIsTemplateListOpen(true);
    };

    const onImportManagerReviewTemplate = () => {
        setActiveTemplateType(EvaluationType.MANAGER_EVALUATION);
        setIsTemplateListOpen(true);
    };

    const onStartBuilding = async (templateType: EvaluationTypeValue) => {
        try {
            setCreatingTemplateType(templateType);
            const skillSelectorOption =
                templateType === EvaluationType.MANAGER_EVALUATION
                    ? ManagerAssessmentSkillSelectorOption.ALL_SKILLS
                    : undefined;
            const response = await createTemplate({
                reviewType: templateType,
                skillSelectorOption,
            });

            const { id: templateId } = response;

            showSuccessToast({
                message: t('templateManagement.template.message.createTemplateSuccess'),
            });

            navigate({
                path: `${RouteName.TemplateQuestionnaire}/${templateId}` as RouteNameType,
                state: { templateId },
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setCreatingTemplateType(null);
        }
    };

    const onPreviewAmelio = (reviewTemplate: ReviewTemplate) => {
        setPreviewedTemplate(reviewTemplate);
        setIsPreviewOpen(true);
    };

    const onConfirmImport = async () => {
        if (!previewedTemplate) {
            return;
        }

        try {
            await importAmelioTemplate(previewedTemplate.id);

            showSuccessToast({
                message: t('templateManagement.template.message.importTemplateSuccess'),
            });

            setIsPreviewOpen(false);
            setIsTemplateListOpen(false);
        } catch (error) {
            handleError({ error });
        }
    };

    const onEditMeetingPlanClick = (templateId: string) => {
        navigate({
            path: `${RouteName.MeetingPlanTemplate}/${templateId}` as RouteNameType,
        });
    };

    const onEditClick = (templateId: string) => {
        navigate({
            path: `${RouteName.TemplateQuestionnaire}/${templateId}` as RouteNameType,
            state: { templateId },
        });
    };

    const onStatusChange = async (reviewTemplate: ReviewTemplate) => {
        try {
            await updateTemplate({
                ...reviewTemplate,
                status:
                    reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                        ? ReviewTemplateStatus.INACTIVE
                        : ReviewTemplateStatus.ACTIVE,
            });

            showSuccessToast({
                message: t(
                    reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                        ? 'templateManagement.template.message.deactivateSuccess'
                        : 'templateManagement.template.message.activateSuccess',
                ),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onDeleteClick = (reviewTemplate: ReviewTemplate) => {
        setTemplateToDelete(reviewTemplate);
    };

    const onConfirmDelete = async () => {
        if (!templateToDelete) {
            return;
        }

        try {
            await deleteTemplate(templateToDelete.id);

            showSuccessToast({
                message: t('templateManagement.template.message.deleteSuccess'),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setTemplateToDelete(undefined);
        }
    };

    /** Only the section whose "start from scratch" was pressed shows its card as working. */
    const isCreatingSection = (templateType: EvaluationTypeValue) =>
        isCreatingTemplate && creatingTemplateType === templateType;

    const templateCardActions = (reviewTemplate: ReviewTemplate): OptionProps[] => {
        return [
            {
                icon: <Icon name={'edit-edit-2'} />,
                id: CommonActions.EDIT,
                label: t('common.edit'),
                onClick: () => onEditClick(reviewTemplate.id),
            },
            {
                icon: (
                    <Icon
                        name={
                            reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                                ? 'essential-close-circle'
                                : 'essential-checkmark'
                        }
                    />
                ),
                id: 'status',
                label:
                    reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                        ? t('common.deactivate')
                        : t('common.activate'),
                onClick: () => onStatusChange(reviewTemplate),
            },
            {
                icon: (
                    <Icon
                        name={'essential-trash'}
                        color={Colors.ERROR}
                    />
                ),
                id: CommonActions.DELETE,
                label: t('common.delete'),
                danger: true,
                onClick: () => onDeleteClick(reviewTemplate),
            },
        ];
    };

    useFetchErrorsHandler({
        errors: [
            isFetchingTemplates360Error,
            isFetchingTemplatesManagerEvaluationError,
            isFetchingTemplatesSelfEvaluationError,
            isFetchingAmelio360TemplatesError,
            isFetchingAmelioManagerReviewTemplatesError,
            isFetchingAmelioSelfReviewTemplatesError,
            isFetchingSelectedAmelioTemplateQuestionsError,
            isFetchingNextPageTemplates360Error,
            isFetchingNextPageTemplatesManagerEvaluationError,
            isFetchingNextPageTemplatesSelfEvaluationError,
        ],
    });

    return (
        <>
            <ManageTemplates
                evaluation360={{
                    templates: company360Templates,
                    isLoading: isFetchingTemplates360,
                    isCreatingTemplate: isCreatingSection(EvaluationType.EVALUATION_360),
                    hasMoreTemplates: hasMoreTemplates360,
                    isFetchingNextPage: isFetchingNextPageTemplates360,
                    onImport: onImport360Template,
                    fetchMore: fetchMore360Templates,
                }}
                selfEvaluation={{
                    templates: companySelfTemplates,
                    isLoading: isFetchingTemplatesSelfEvaluation,
                    isCreatingTemplate: isCreatingSection(EvaluationType.SELF_EVALUATION),
                    hasMoreTemplates: hasMoreTemplatesSelfEvaluation,
                    isFetchingNextPage: isFetchingNextPageTemplatesSelfEvaluation,
                    onImport: onImportSelfReviewTemplate,
                    fetchMore: fetchMoreSelfReviewTemplates,
                }}
                managerEvaluation={{
                    templates: companyManagerTemplates,
                    isLoading: isFetchingTemplatesManagerEvaluation,
                    isCreatingTemplate: isCreatingSection(EvaluationType.MANAGER_EVALUATION),
                    hasMoreTemplates: hasMoreTemplatesManagerEvaluation,
                    isFetchingNextPage: isFetchingNextPageTemplatesManagerEvaluation,
                    onImport: onImportManagerReviewTemplate,
                    fetchMore: fetchMoreManagerReviewTemplates,
                }}
                meetingPlans={{
                    templates: meetingPlanTemplates ?? [],
                    isLoading: isLoadingMeetingPlanTemplates,
                    isError: isFetchingMeetingPlanTemplatesError,
                    onSelectTemplate: onEditMeetingPlanClick,
                    onCreateTemplate: () => setIsNewMeetingPlanOpen(true),
                    onImportTemplate: () => setIsMeetingPlanCatalogOpen(true),
                }}
                templateCardActions={templateCardActions}
                onStartBuilding={onStartBuilding}
                onTemplateCardClick={onEditClick}
            />

            {/* The preview is declared before the list it opens from: when both close together
                (an import, or Escape), it releases its page scroll lock first, so the list's release
                restores the page's own scrolling instead of the lock the preview had captured. */}
            <TemplateQuestionModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                questions={selectedAmelioReviewTemplate?.questions || []}
                isLoading={isFetchingSelectedAmelioTemplateQuestions}
                templateName={previewedTemplate?.title || {}}
                templateDescription={previewedTemplate?.description || ''}
                isCreatingTemplate={isImportingAmelioTemplate}
                onImport={() => void onConfirmImport()}
            />

            <TemplateListModal
                isOpen={isTemplateListOpen}
                onClose={() => setIsTemplateListOpen(false)}
                title={templateListTitle()}
                subtitle={t(
                    'templateManagement.template.importTemplatemodal.importTemplateSubtitle',
                )}
                iconName={'electronics-monitor'}
                templates={modalTemplates}
                toTile={toTemplateListTile}
                isLoading={
                    isLoadingAmelio360Templates ||
                    isLoadingAmelioManagerReviewTemplates ||
                    isLoadingAmelioSelfReviewTemplates
                }
                onPreview={onPreviewAmelio}
                dataTestId={'template-list-modal'}
            />

            {canUseMeetingPlans && (
                <>
                    <NewMeetingPlanModal
                        isOpen={isNewMeetingPlanOpen}
                        onClose={() => setIsNewMeetingPlanOpen(false)}
                    />

                    <MeetingPlanCatalogModal
                        isOpen={isMeetingPlanCatalogOpen}
                        onClose={() => setIsMeetingPlanCatalogOpen(false)}
                    />
                </>
            )}

            {Boolean(cycleEvaluationId) && (
                <div className={'mt-4 flex items-end justify-between px-15 pb-8'}>
                    <div className={'flex'}>
                        <ButtonSecondary
                            className={'mr-2'}
                            dataTestId={'back-button'}
                            onClick={() =>
                                navigate({
                                    path: `${RouteName.CycleEvaluationSelectTemplate}/${cycleEvaluationId}`,
                                })
                            }
                            leftIcon={
                                <Icon
                                    name={'arrow-arrow-left'}
                                    className={'mr-2'}
                                />
                            }
                        >
                            {t('common.back')}
                        </ButtonSecondary>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={Boolean(templateToDelete)}
                title={t('common.deleteConfirmTitle')}
                message={t('templateManagement.template.deleteConfirmMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDelete}
                onCancel={() => setTemplateToDelete(undefined)}
                isLoading={isDeletingTemplate}
                isDestructive
            />
        </>
    );
};
